import { useState } from 'react'
import { regulationId, regulationLabel, regulationSelection } from '@/utils/regulationLabels'
import PrepackChoiceCard from './PrepackChoiceCard'

/**
 * Select assigned regulations for an operation. The choices fold under a
 * header that summarises the selection, in the warning colour when nothing is
 * selected; `disabled` locks the choices, never the disclosure.
 */
export default function PrepackRegulationPicker({
  regulations = [],
  value = [],
  onChange,
  disabled = false,
  label = 'Target regulations',
}) {
  const [open, setOpen] = useState(false)
  const selectedValues = Array.isArray(value) ? value : []
  const availableIds = regulations.map(regulationId).filter(Boolean)
  const selectedAvailable = availableIds.filter((id) => selectedValues.includes(id))
  const allSelected = availableIds.length > 0 && selectedAvailable.length === availableIds.length
  const selection = availableIds.length > 0
    ? regulationSelection(selectedAvailable.length, availableIds.length)
    : { state: 'unassigned', summary: 'No regulations assigned' }
  const handleToggleAll = () => {
    if (disabled) return
    onChange?.(allSelected ? [] : [...availableIds])
  }

  const handleToggle = (id) => {
    if (disabled || !id) return
    onChange?.(selectedValues.includes(id)
      ? selectedValues.filter((item) => item !== id)
      : [...selectedValues, id])
  }

  return (
    <PrepackChoiceCard
      title={label}
      summary={selection.summary}
      // Save and Publish are off without a target; the folded header says why.
      summaryTone={selection.state === 'none' ? 'warning' : 'muted'}
      open={open}
      onToggle={() => setOpen((current) => !current)}
      headingLevel={3}
      size="sm"
    >
      {availableIds.length > 0 ? (
        <>
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              disabled={disabled}
              onClick={handleToggleAll}
              aria-label={`${allSelected ? 'Clear' : 'Select'} all regulations`}
              className="text-xs font-medium text-[var(--prepacks-accent-text)] hover:underline disabled:pointer-events-none disabled:opacity-40"
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
            {regulations.map((regulation) => {
              const id = regulationId(regulation)
              if (!id) return null
              const isSelected = selectedValues.includes(id)
              return (
                <label
                  key={id}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                    isSelected
                      ? 'border-[var(--prepacks-accent-text)] bg-[var(--wf-color-action-soft)] text-[var(--wf-color-text)]'
                      : 'border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] text-[var(--prepacks-muted)] hover:border-[var(--prepacks-muted)]'
                  } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    value={id}
                    disabled={disabled}
                    checked={isSelected}
                    onChange={() => handleToggle(id)}
                    aria-label={regulationLabel(regulation)}
                    className="h-3.5 w-3.5 rounded border-[var(--prepacks-border)] text-[var(--prepacks-accent)] focus:ring-[var(--prepacks-accent)]"
                  />
                  <span>{regulationLabel(regulation)}</span>
                </label>
              )
            })}
          </div>
        </>
      ) : (
        <p className="text-xs text-[var(--prepacks-muted)]">No regulations are assigned to this organization.</p>
      )}
    </PrepackChoiceCard>
  )
}
